/**
 * THE ONE PLACE A FAILURE IS REPORTED TO THE USER.
 *
 * Every user-visible failure goes through `reportError`. It does four things that the ~145
 * hand-written `ToastUtility.show(\`Failed to X. Error: ${e}\`)` calls it replaces could not:
 *
 *   1. Puts a **stable code** in front of the user, so the same failure is the same words
 *      everywhere and support can be told exactly which one it was.
 *   2. Keeps the raw `AxiosError: Request failed with status code 500` **out of the message**.
 *      It was never information; it was noise that made the real message harder to read.
 *   3. Builds the **question the support assistant should be asked**, so "Ask the assistant
 *      about this" hands over an exact code rather than prose to be guessed at.
 *   4. Keeps endpoint and status in `diagnostics`, which travels to the escalation e-mail and
 *      **never to the model** — `SupportPrompt.cs` forbids the assistant from mentioning HTTP
 *      detail, and given a status code it will recite it at an operator.
 *
 * @see src/utility/errorCatalog.ts — the code table.
 */

import { ErrorDescriptor, refusalFor } from "./errorCatalog";
import { raiseErrorDialog } from "./errorDialogBus";
import { logger } from "./logger";
import { notify } from "./notify";
import { isSupportAssistantEnabled } from "./supportAssistantAccess";
import {
  isSupportAssistantMounted,
  openSupportAssistant,
} from "./supportAssistantBus";

export interface ErrorContext {
  /** The production order, where there is one. Shown to the user and sent to the assistant. */
  readonly jobNumber?: string;
  /** The resource/operation, where there is one. */
  readonly resource?: string;
  /**
   * A message the SERVER produced. This is the only error text worth showing: unlike an Axios
   * string it was written for a person. `JobAction.handleError` already routes these to
   * `ErrorModal`, which is why the modal has always been the more useful of the two surfaces.
   *
   * Optional since it is now read off the `cause` when it is not passed — see below.
   */
  readonly serverMessage?: string;
  /**
   * A refusal slug the server sent, if it sent one. Also read off the `cause` when not passed.
   * Resolves to a more specific code; see `REFUSAL_BY_REASON` in the catalogue.
   */
  readonly serverReason?: string;
  /** Path only — no host, no query. Diagnostics only; never reaches the model. */
  readonly endpoint?: string;
  readonly status?: number;
}

/** What `reportError` produces. Also what `ErrorModal` and the assistant bus consume. */
export interface ReportedError {
  readonly code: string;
  readonly operation: string;
  /** What the user is shown. Always ends with the code in brackets. */
  readonly message: string;
  /** What the assistant is asked. Contains the code; contains no HTTP detail. */
  readonly question: string;
  /** For the escalation e-mail. Contains the HTTP detail. Never sent to the model. */
  readonly diagnostics: string;
}

/**
 * Turn a descriptor plus whatever went wrong into a reportable failure.
 *
 * Pure — it neither toasts nor logs. `reportError` does that; callers that want to put the
 * result in an `ErrorModal` instead use this directly.
 */
export const buildReportedError = (
  descriptor: ErrorDescriptor,
  cause?: unknown,
  context: ErrorContext = {}
): ReportedError => {
  const where = context.jobNumber ? ` on ${context.jobNumber}` : "";

  // The server's own message is the only cause text a user benefits from seeing. An Axios
  // string tells them the network had an opinion, which they already knew.
  //
  // ⚠️ READ OFF THE CAUSE WHEN THE CALLER DID NOT PASS IT. Roughly a hundred call sites report
  // a failure; only a handful ever threaded `serverMessage` through, so at nearly all of them a
  // sentence the server had already written for this exact person was thrown away and replaced
  // with "Load the parts list failed." Defaulting it on is the one change here that improves
  // every one of those at once.
  const said = context.serverMessage?.trim() || serverMessageOf(cause);

  // ...but only if it SAYS something. See UNINFORMATIVE.
  //
  // ⚠️ THE FILTER IS HERE AND NOT IN `serverMessageOf`, WHICH IS NOT A TIDINESS QUESTION.
  // `JobAction.handleError` uses `serverMessageOf` to decide between the coded toast and the
  // ErrorModal, and the modal is the surface that carries "Ask the assistant about this".
  // Filtering there would send every generic failure to a toast and quietly take that button
  // away from precisely the failures somebody most needs help with. Filtering here leaves the
  // routing alone and gives the modal the clean coded message instead of the empty sentence.
  const detail = said && !UNINFORMATIVE.test(said) ? said : undefined;

  // A REFUSAL REPORTS UNDER ITS OWN CODE, NOT THE ACTION'S. The action's code says what was
  // being attempted; when the server refused on purpose it also said WHY, and the why is what
  // the assistant needs. Everything else here still uses `descriptor` — in particular the
  // "What I was doing" line, which must stay the attempt: a refusal descriptor's `operation`
  // describes the refusal, not what the person was trying to do.
  const refusal = refusalFor(context.serverReason ?? serverReasonOf(cause));
  const code = refusal?.code ?? descriptor.code;

  const message = detail
    ? `${descriptor.operation}${where} failed: ${detail} (${code})`
    : `${descriptor.operation}${where} failed. (${code})`;

  const question = [
    `I got error ${code} in CSM.`,
    `What I was doing: ${descriptor.operation}`,
    context.jobNumber ? `Production order: ${context.jobNumber}` : null,
    context.resource ? `Resource: ${context.resource}` : null,
    detail ? `What it said: ${detail}` : null,
    "",
    "What does this mean and what should I do?",
  ]
    .filter((line) => line !== null)
    .join("\n");

  const diagnostics = [
    `Code: ${code}`,
    // Support searches on the action as often as on the code, and once a refusal reports under
    // CSM-JOB-02x the action's own code is nowhere else in the record.
    refusal ? `Refused action code: ${descriptor.code}` : null,
    `Operation: ${descriptor.operation}`,
    context.jobNumber ? `Production order: ${context.jobNumber}` : null,
    context.resource ? `Resource: ${context.resource}` : null,
    context.endpoint ? `Endpoint: ${context.endpoint}` : null,
    context.status ? `Status: ${context.status}` : null,
    // `said`, not `detail`: diagnostics is the full record, so it keeps the generic sentence
    // the user is spared. "The server answered, and had nothing to say" is itself a fact worth
    // having in an escalation e-mail — its absence reads as "the server never answered".
    said ? `Server message: ${said}` : null,
    cause ? `Cause: ${stringifyCause(cause)}` : null,
  ]
    .filter((line) => line !== null)
    .join("\n");

  return { code, operation: descriptor.operation, message, question, diagnostics };
};

/**
 * Report a failure: log it with full detail, show the user the coded message.
 *
 * Returns the `ReportedError` so a caller can also hand it to `openSupportAssistant`. Callers
 * that only want it shown can ignore the return.
 */
export const reportError = (
  descriptor: ErrorDescriptor,
  cause?: unknown,
  context: ErrorContext = {}
): ReportedError => {
  const reported = buildReportedError(descriptor, cause, context);

  // The log keeps everything, including the cause and the HTTP detail the user is spared.
  logger.error(reported.diagnostics, cause);
  present(reported);

  return reported;
};

/**
 * EVERY FAILURE GETS THE DIALOG; THE TOAST IS WHAT IS LEFT WHEN THERE IS NO DIALOG TO GET.
 *
 * ⚠️ THE FALLBACK IS NOT DECORATIVE AND MUST NOT BE DELETED. `raiseErrorDialog` answers `false`
 * whenever no `ErrorDialogHost` is mounted — every unit test that renders one component, and any
 * tree that never reached `App.tsx`. Dropping the fallback would make `reportError` show the
 * user *nothing* in those cases, which is the one outcome worse than the toast this replaced.
 *
 * A toast was always the wrong surface for a failure: it disappears on a timer, cannot be
 * re-read, and takes the code support asks for with it. The dialog has to be dismissed, so the
 * failure is seen; it stays put, so the code can be read out; and it is where "Ask the assistant
 * about this" already lives.
 */
const present = (reported: ReportedError): void => {
  if (raiseErrorDialog(reported)) return;

  toastReported(reported);
};

/** Appended to a toast that can be pressed, because nothing else about it says so. */
const ASK_HINT = " — tap for help";

/**
 * A toast that is pressed for help is 22 characters longer and has to be noticed before it can
 * be acted on, so 4s is not enough time to read it, decide, and reach it.
 */
const CLICKABLE_TIMEOUT = 7000;

/**
 * THE TOAST, AND THE WAY INTO THE ASSISTANT FROM IT.
 *
 * ⚠️ THE TOAST IS THE SURFACE MOST FAILURES ACTUALLY LAND ON, which is why it needed this.
 * `ErrorModal` carried "Ask the assistant about this" and the toast carried nothing — but
 * `JobAction.handleError` only raises the dialog when the BFF wrote a sentence of its own, and
 * the ~145 other call sites never raise it at all. So a 500 with an empty body, a network
 * failure, a gateway's HTML error page, a throw that never reached the server — every failure
 * with nothing to quote, which is exactly the set nobody can act on unaided — produced a red
 * bubble with a code in it and no way to ask about the code. "Start a job on 803 failed.
 * (CSM-JOB-001)" and a dead end.
 *
 * Both gates, for the reason `ErrorModal` states at length: `isSupportAssistantEnabled()` says
 * the feature exists in this environment, `isSupportAssistantMounted()` says the panel is there
 * to receive the question. Failing either, the toast is exactly the toast it was before — no
 * hint, no cursor, nothing that invites a press that would do nothing.
 *
 * The hint goes AFTER the code, never before it: the code is the token support asks for and
 * `findErrorCode` matches anywhere in the string, so a suffix costs it nothing, while a prefix
 * would push the code toward the far end of a narrow toast.
 */
const toastReported = (reported: ReportedError): void => {
  if (!isSupportAssistantEnabled() || !isSupportAssistantMounted()) {
    notify.error(reported.message);
    return;
  }

  notify.error(`${reported.message}${ASK_HINT}`, CLICKABLE_TIMEOUT, () =>
    // Seeds the composer and does NOT send it — the user presses Send themselves, having read
    // and if necessary reworded the question. `diagnostics` rides along for the escalation
    // e-mail and is never shown to them or to the model.
    openSupportAssistant({
      question: reported.question,
      diagnostics: reported.diagnostics,
    })
  );
};

/** `error.response.data`, whatever shape it is in. */
const bodyOf = (error: unknown): unknown =>
  (error as { response?: { data?: unknown } } | null)?.response?.data;

/**
 * The Function App's placeholder for "something broke and we are not saying what". Around
 * forty endpoints return exactly this sentence from a blanket `catch (Exception)`.
 *
 * ⚠️ `buildReportedError` TREATS IT AS NO MESSAGE AT ALL, which is what it is. It says nothing
 * the code does not already say, and the assistant is handed it as "What it said: …" — a worse
 * question than no quote at all, since a model reading "could not be processed" will answer
 * about a processing failure. Dropping it is also what lets `buildReportedError` default the
 * server message ON at every call site without filling the toasts with it.
 */
const UNINFORMATIVE = /^the request could not be processed\b/i;

/**
 * Pull the server's own message out of an Axios-shaped error, if there is one.
 *
 * `error.response.data` is the message the BFF wrote for a person. Anything else — an HTML
 * error page, undefined, an object with nothing to say — is not worth showing, so it is dropped
 * rather than stringified into noise.
 *
 * ⚠️ TWO BODY SHAPES, ON PURPOSE. Nearly every 400 in the Function App is a bare string. The
 * job-action endpoints answer a REFUSAL with `{ reason, message }` instead, because the reason
 * slug has nowhere else to ride (`ActionRefusedException.cs`). Reading only the string shape —
 * which this did — would drop the refusal's message on the floor and show the operator a bare
 * "Start a job failed.", which is worse than what it replaced.
 */
export const serverMessageOf = (error: unknown): string | undefined => {
  const data = bodyOf(error);

  const text =
    typeof data === "string"
      ? data.trim()
      : typeof (data as { message?: unknown } | null)?.message === "string"
        ? ((data as { message: string }).message).trim()
        : "";

  if (!text || text.startsWith("<")) return undefined;

  return text;
};

/**
 * The refusal slug the server sent, if this was a refusal rather than a fault.
 *
 * Never shown to anyone and never sent to the model: it is internal vocabulary, and
 * `SupportPrompt.cs` forbids the assistant from saying that kind of thing out loud. Its only
 * job is to resolve to an error code in the catalogue.
 */
export const serverReasonOf = (error: unknown): string | undefined => {
  const reason = (bodyOf(error) as { reason?: unknown } | null)?.reason;
  return typeof reason === "string" && reason.trim() ? reason.trim() : undefined;
};

/** Status code from an Axios-shaped error, for diagnostics only. */
export const statusOf = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } } | null)?.response?.status;

/**
 * The sentence the server refused a request with, when the request broke a rule: a 400 whose
 * body says something of its own. Undefined for everything else.
 *
 * For a screen that shows such a sentence beside the field it is about, word for word, with no
 * code and no dialog. That is right for "Enter a name for the tool." and wrong for a fault, and
 * the Function App answers both with a 400: its blanket catch sends the UNINFORMATIVE sentence
 * above. So that sentence is nothing here, and the caller reports it like any other failure.
 */
export const serverRefusalOf = (error: unknown): string | undefined => {
  if (statusOf(error) !== 400) return undefined;

  const said = serverMessageOf(error);
  return said && !UNINFORMATIVE.test(said) ? said : undefined;
};

const stringifyCause = (cause: unknown): string => {
  if (cause instanceof Error) return `${cause.name}: ${cause.message}`;
  if (typeof cause === "string") return cause;
  try {
    return JSON.stringify(cause);
  } catch {
    return String(cause);
  }
};
